import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@doccareplus/i18n';
import { Button, Card } from '@doccareplus/ui';
import {
  profileName,
  useFoundProfilesQuery,
  usePortalHomeQuery,
  type PortalMedicationItemDto,
  type PortalNextAppointmentDto,
  type PortalRecentVisitDto,
} from '@/features/profiles';
import { PortalLoadError } from '@/shared/ui/PortalLoadError';
import { HomePageSkeleton } from './HomeSkeletons';

interface QuickLink {
  to: string;
  labelKey: string;
  fallback: string;
  paths: string[];
}

const QUICK_LINKS: QuickLink[] = [
  { to: '/appointments', labelKey: 'tabs.appointments', fallback: 'Visits', paths: ['M8 2v4', 'M16 2v4', 'M3 10h18', 'M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z'] },
  { to: '/records', labelKey: 'tabs.records', fallback: 'Records', paths: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6'] },
  { to: '/billing', labelKey: 'tabs.billing', fallback: 'Billing', paths: ['M2 5h20v14H2z', 'M2 10h20'] },
];

function NextAppointmentHero({
  appointment,
  clinicPhone,
  timezone,
  onReschedule,
}: {
  appointment: PortalNextAppointmentDto;
  clinicPhone: string | null;
  timezone: string;
  onReschedule?: () => void;
}) {
  const { t } = useTranslation('portal');
  const when = new Date(appointment.startTime).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  });

  return (
    <Card className="overflow-hidden border-0 bg-gradient-to-br from-[var(--brand-600)] to-[var(--brand-800)] p-5 text-white shadow-[var(--shadow-2)]">
      <div className="text-[11.5px] font-semibold uppercase tracking-[0.06em] opacity-85">
        {t('home.nextAppointment', 'Next appointment')}
      </div>
      <div className="mt-1 text-[19px] font-bold">{when}</div>
      {appointment.reason && <div className="mt-0.5 text-[13px] opacity-90">{appointment.reason}</div>}
      <div className="mt-4 flex flex-wrap gap-2">
        {onReschedule && (
          <Button className="border-white/20 bg-white/15 text-white hover:bg-white/25" variant="outline" onClick={onReschedule}>
            {t('appointments.reschedule', 'Reschedule')}
          </Button>
        )}
        {clinicPhone && (
          <Button
            className="border-white bg-white text-[var(--brand-700)] hover:bg-white/90"
            variant="outline"
            onClick={() => {
              window.location.href = `tel:${clinicPhone}`;
            }}
          >
            {t('home.callClinic', 'Call clinic')}
          </Button>
        )}
      </div>
    </Card>
  );
}

function AllergyBanner({ summary }: { summary: string }) {
  const { t } = useTranslation('portal');
  return (
    <Card className="border-[var(--danger)] bg-[var(--danger-bg)] p-4">
      <div className="text-[13px] font-bold text-[var(--danger)]">
        {t('home.allergy', 'Allergy: {{summary}}', { summary })}
      </div>
    </Card>
  );
}

function MedicationsCard({ medications }: { medications: PortalMedicationItemDto[] }) {
  const { t } = useTranslation('portal');
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-[15px] font-bold">{t('home.activeMedication', 'Active medication')}</h2>
      <Card className="p-1.5">
        {medications.map((medicine, index) => (
          <div
            key={medicine.medicineName}
            className={`px-3 py-2.5 ${index < medications.length - 1 ? 'border-b border-[var(--border)]' : ''}`}
          >
            <div className="text-[13.5px] font-semibold">{medicine.medicineName}</div>
            {medicine.detail && <div className="mt-0.5 text-[12px] text-[var(--text-2)]">{medicine.detail}</div>}
            {medicine.occurred && <div className="mt-0.5 text-[11.5px] text-[var(--text-3)]">{medicine.occurred}</div>}
          </div>
        ))}
      </Card>
    </section>
  );
}

function RecentVisitsSection({ visits }: { visits: PortalRecentVisitDto[] }) {
  const { t } = useTranslation('portal');
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-[15px] font-bold">{t('home.recentVisits', 'Recent visits')}</h2>
      {visits.map((visit) => (
        <Card key={`${visit.visitDate}-${visit.providerDisplayName}`} className="p-3.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[14px] font-semibold">{visit.diagnosis ?? visit.providerDisplayName}</span>
            <span className="whitespace-nowrap text-[12px] text-[var(--text-3)]">
              {new Date(visit.visitDate).toLocaleDateString()}
            </span>
          </div>
          <div className="mt-0.5 text-[12.5px] text-[var(--text-2)]">{visit.providerDisplayName}</div>
        </Card>
      ))}
    </section>
  );
}

export function HomePage() {
  const { t } = useTranslation('portal');
  const navigate = useNavigate();
  const home = usePortalHomeQuery();
  const found = useFoundProfilesQuery(!home.isLoading && (home.data?.profiles.length ?? 0) === 0);

  if (home.isLoading) {
    return <HomePageSkeleton label={t('home.loading', 'Loading your home summary')} />;
  }

  if (home.isError) {
    return <PortalLoadError onRetry={() => void home.refetch()} />;
  }

  const active = home.data?.activeProfile ?? null;
  const hasProfiles = (home.data?.profiles.length ?? 0) > 0;
  const summary = home.data?.summary ?? null;

  return (
    <div className="flex flex-col gap-5">
      <div className="px-0.5">
        {active && (
          <div className="text-[12.5px] text-[var(--text-2)]">
            {t('home.viewingRelationship', 'Viewing {{relationship}} profile', {
              relationship: t(
                `profile.relationship.${active.relationshipType.toLowerCase()}`,
                active.relationshipType
              ).toLowerCase(),
            })}
          </div>
        )}
        <h1 className="mt-0.5 text-[24px] font-bold tracking-[-0.01em]">
          {active ? profileName(active) : t('home.greeting', 'Welcome')}
        </h1>
        <p className="text-[12.5px] text-[var(--text-3)]">
          {active ? `${active.clinicName} · ${active.patientDisplayName}` : t('home.subtitle', 'Your health at a glance.')}
        </p>
        <Button className="mt-3" size="sm" variant="outline" onClick={() => navigate('/find-care')}>
          {t('home.findClinic', 'Find a clinic')}
        </Button>
      </div>

      {active?.isArchived && (
        <div
          role="status"
          className="rounded-[12px] border border-[var(--warning)] bg-[var(--warning-bg)] px-4 py-3 text-[13px] leading-5 text-[var(--text)]"
        >
          <span className="font-semibold">
            {t('home.archivedTitle', 'This clinic record is archived.')}
          </span>{' '}
          {t('home.archivedDescription', 'You can review historical records in read-only mode.')}
        </div>
      )}

      {active && home.data?.isActiveProfilePortalAvailable === false ? (
        <Card role="status" className="border-[var(--warning)] bg-[var(--warning-bg)] p-5">
          <div className="text-[15px] font-bold">
            {t('profile.portalUnavailableTitle', 'Portal access is not available for this clinic')}
          </div>
          <p className="mt-1 text-[13px] leading-5 text-[var(--text-2)]">
            {t('profile.portalUnavailableDescription', 'Your profile stays connected, but this clinic has paused online records and appointment access.')}
          </p>
        </Card>
      ) : active && summary?.nextAppointment ? (
        <NextAppointmentHero
          appointment={summary.nextAppointment}
          clinicPhone={summary.clinicPhone}
          timezone={summary.timezone}
          onReschedule={active.isArchived ? undefined : () => navigate('/appointments')}
        />
      ) : active ? (
        <Card className="overflow-hidden border-0 bg-gradient-to-br from-[var(--brand-600)] to-[var(--brand-800)] p-5 text-white shadow-[var(--shadow-2)]">
          <div className="text-[11.5px] font-semibold uppercase tracking-[0.06em] opacity-85">
            {t('home.connectedRecord', 'Clinic record connected')}
          </div>
          <div className="mt-1 text-[19px] font-bold">{active.clinicName}</div>
          <div className="mt-1 max-w-[52ch] text-[12.5px] leading-5 opacity-85">
            {t('home.connectedHint', 'Appointments, prescriptions and billing stay scoped to this clinic profile.')}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {!active.isArchived && (
              <Button className="border-white/20 bg-white/15 text-white hover:bg-white/25" variant="outline" onClick={() => navigate('/appointments')}>
                {t('appointments.request', 'Request appointment')}
              </Button>
            )}
            <Button className="border-white bg-white text-[var(--brand-700)] hover:bg-white/90" variant="outline" onClick={() => navigate('/records')}>
              {t('home.viewRecords', 'View records')}
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="p-5">
          <p className="text-[15px] font-bold">{t('home.noProfileTitle', 'Connect your profile')}</p>
          {found.data?.profiles.length ? (
            <>
              <p className="mt-1 text-[13px] leading-5 text-[var(--text-2)]">
                {t('home.foundAtClinic', 'We found a clinic record that matches your verified phone or email.')}
              </p>
              <div className="my-4 flex flex-col gap-2">
                {found.data.profiles.map((profile) => (
                  <div key={profile.patientId} className="rounded-[10px] bg-[var(--surface-2)] px-3.5 py-3">
                    <div className="text-[13.5px] font-bold">{profile.clinicName}</div>
                    <div className="text-[11.5px] text-[var(--text-3)]">
                      {profile.patientDisplayName}{profile.dobYear ? ` · born ${profile.dobYear}` : ''}
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="primary" onClick={() => navigate('/profile')}>
                {t('home.reviewAndConnect', 'Review and connect')}
              </Button>
            </>
          ) : (
            <>
              <p className="mb-4 mt-1 text-[13px] leading-5 text-[var(--text-3)]">
                {hasProfiles
                  ? t('home.selectProfileHint', 'Choose which profile to view.')
                  : t('home.connectProfileHint', 'Connect the clinic profile registered to your verified phone number.')}
              </p>
              <Button variant="primary" onClick={() => navigate('/profile')}>
                {hasProfiles ? t('home.chooseProfile', 'Choose profile') : t('home.connectProfile', 'Find clinic records')}
              </Button>
            </>
          )}
        </Card>
      )}

      {active && summary?.allergySummary && <AllergyBanner summary={summary.allergySummary} />}

      {active && (
        <section>
          <h2 className="mb-3 text-[15px] font-bold">{t('home.quickAccess', 'Quick access')}</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {QUICK_LINKS.map((link) => (
              <Button
                key={link.to}
                variant="outline"
                onClick={() => navigate(link.to)}
                className="h-auto min-h-[102px] flex-col items-start gap-3 rounded-[14px] bg-[var(--surface)] p-4 text-left"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] bg-[var(--primary-weak)] text-[var(--primary)]">
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    {link.paths.map((d, i) => <path key={i} d={d} />)}
                  </svg>
                </span>
                <span className="text-[13.5px] font-semibold text-[var(--text)]">{t(link.labelKey, link.fallback)}</span>
              </Button>
            ))}
          </div>
        </section>
      )}

      {active && summary && summary.currentMedications.length > 0 && (
        <MedicationsCard medications={summary.currentMedications} />
      )}

      {active && summary && summary.recentVisits.length > 0 && (
        <RecentVisitsSection visits={summary.recentVisits} />
      )}
    </div>
  );
}
